<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { formatDateTime, formatNumber } from '../utils/qprecoFormatters';
import { SHARE_EXPIRATION_OPTIONS } from '../utils/qprecoConstants';
import {
  shareAuditEvents,
  shareAuditSummary,
  shareCreatorLabel,
  shareEventLabel,
  shareExpirationLabel,
  shareStatusLabel,
  shareStatusTone,
  shareTokenLabel,
  shareViewLimitLabel,
} from '../utils/qprecoShares';

defineProps({
  activeSharesCount: { type: Number, default: 0 },
  creatingShare: { type: Boolean, default: false },
  currentIssuedShare: { type: Object, default: null },
  sharesLoading: { type: Boolean, default: false },
  sharesError: { type: Boolean, default: false },
  shareListSections: { type: Array, default: () => [] },
});

defineEmits(['create', 'copy', 'reload', 'revoke']);

const expiresInDays = defineModel('expiresInDays', {
  type: [String, Number],
  default: '30',
});
const viewLimit = defineModel('viewLimit', {
  type: [String, Number],
  default: '',
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Compartilhamentos">
    <template #titleAction>
      <span
        class="rounded-full bg-lais-purple-50 px-3 py-1 text-[12px] font-semibold text-lais-primary"
      >
        {{ formatNumber(activeSharesCount) }} ativos
      </span>
    </template>

    <div class="grid gap-4">
      <div
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-muted px-3 py-3"
      >
        <p class="text-[13px] font-semibold text-lais-text">
          Criar link público do relatório
        </p>
        <div class="grid gap-2 sm:grid-cols-2">
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Expiração
            <select
              v-model="expiresInDays"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
            >
              <option
                v-for="option in SHARE_EXPIRATION_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label
            class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
          >
            Limite de acessos
            <input
              v-model="viewLimit"
              type="number"
              min="1"
              inputmode="numeric"
              class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
              placeholder="Sem limite"
            />
          </label>
        </div>
        <LaisPrimaryButton
          icon="i-lucide-link"
          :is-loading="creatingShare"
          @click="$emit('create')"
        >
          Criar link
        </LaisPrimaryButton>
      </div>

      <div
        v-if="currentIssuedShare?.public_url && currentIssuedShare?.active"
        class="rounded-lais border border-emerald-200 bg-emerald-50 px-3 py-3"
      >
        <p class="text-[13px] font-semibold text-emerald-900">
          Link criado agora
        </p>
        <a
          :href="currentIssuedShare.public_url"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-1 block break-all text-[12px] font-semibold text-emerald-800 underline"
        >
          {{ currentIssuedShare.public_url }}
        </a>
        <div class="mt-3 flex flex-wrap gap-2">
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            icon="i-lucide-copy"
            @click="$emit('copy')"
          >
            Copiar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            size="sm"
            variant="ghost"
            icon="i-lucide-external-link"
            tag="a"
            :href="currentIssuedShare.public_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir
          </LaisPrimaryButton>
        </div>
      </div>

      <div
        v-if="sharesLoading"
        class="rounded-lais border border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
      >
        <span
          class="i-lucide-loader-circle mr-2 inline-block size-4 animate-spin text-lais-primary"
          aria-hidden="true"
        />
        Carregando compartilhamentos...
      </div>

      <div
        v-else-if="sharesError"
        class="rounded-lais border border-rose-100 bg-rose-50 px-3 py-3 text-[13px] text-rose-800"
      >
        Não foi possível carregar os links deste relatório.
        <LaisPrimaryButton
          class="mt-2"
          size="sm"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          @click="$emit('reload')"
        >
          Recarregar
        </LaisPrimaryButton>
      </div>

      <div v-else class="grid gap-4">
        <section
          v-for="section in shareListSections"
          :key="section.key"
          class="grid gap-2"
        >
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-[13px] font-semibold text-lais-text">
              {{ section.title }}
            </h3>
            <span class="text-[12px] font-semibold text-lais-text-muted">
              {{ formatNumber(section.shares.length) }}
            </span>
          </div>
          <p
            v-if="!section.shares.length"
            class="rounded-lais-sm bg-lais-bg-muted px-3 py-2 text-[12px] text-lais-text-muted"
          >
            {{ section.empty }}
          </p>
          <article
            v-for="share in section.shares"
            :key="share.id"
            class="rounded-lais-sm border border-lais-border-soft px-3 py-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-lais-text">
                  {{ shareTokenLabel(share) }}
                </p>
                <p class="mt-1 text-[12px] text-lais-text-muted break-words">
                  {{ shareExpirationLabel(share) }} ·
                  {{ shareViewLimitLabel(share) }}
                </p>
                <p class="mt-1 text-[12px] text-lais-text-muted break-words">
                  Emitido por {{ shareCreatorLabel(share) }} ·
                  {{ shareAuditSummary(share) }}
                </p>
              </div>
              <span
                class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                :class="shareStatusTone(share)"
              >
                {{ shareStatusLabel(share) }}
              </span>
            </div>

            <ol
              v-if="shareAuditEvents(share).length"
              class="mt-3 grid gap-1 rounded-lais-sm bg-lais-bg-muted px-3 py-2 text-[11px] text-lais-text-muted"
            >
              <li
                v-for="event in shareAuditEvents(share).slice(0, 3)"
                :key="event.id"
              >
                {{ shareEventLabel(event.event_type) }} ·
                {{ formatDateTime(event.occurred_at) }}
              </li>
            </ol>

            <div v-if="share.active" class="mt-3 flex flex-wrap justify-end gap-2">
              <LaisPrimaryButton
                v-if="share.public_url"
                size="sm"
                variant="ghost"
                icon="i-lucide-external-link"
                tag="a"
                :href="share.public_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir
              </LaisPrimaryButton>
              <LaisPrimaryButton
                size="sm"
                variant="ghost"
                icon="i-lucide-ban"
                class="text-rose-700"
                @click="$emit('revoke', share)"
              >
                Revogar
              </LaisPrimaryButton>
            </div>
          </article>
        </section>
      </div>
    </div>
  </LaisCard>
</template>
